/**
 * Operational battery bank schematic (not CAD).
 */

import React from 'react';
import { Empty, Space, Tag, Typography } from 'antd';
import PmvApplyLayoutEmpty from '@/components/pmv/PmvApplyLayoutEmpty';

const { Text } = Typography;

export type BatterySchematicCard = {
  position_id: string;
  position_code: string;
  display_label: string;
  status: 'OCCUPIED' | 'VACANT';
  component_id: string | null;
  component_number: string | null;
  condition_code: string | null;
  battery_type: string | null;
  nominal_voltage: number | null;
  latest_voltage: number | null;
  latest_soh_pct: number | null;
  latest_health_source: string | null;
  attention: string[];
};

export type BatterySchematicResponse = {
  pmv_profile_id: string;
  cards: BatterySchematicCard[];
  empty_reason: string | null;
  note?: string;
};

const BatteryCard: React.FC<{ card: BatterySchematicCard }> = ({ card }) => {
  const vacant = card.status === 'VACANT';
  return (
    <div
      role="group"
      aria-label={`${card.display_label} ${vacant ? 'vacant' : card.component_number || 'occupied'}`}
      style={{
        minWidth: 140,
        maxWidth: 180,
        padding: '10px 12px',
        border: '1px solid var(--ant-color-border, #d9d9d9)',
        borderRadius: 6,
        background: vacant ? 'var(--ant-color-fill-quaternary, #fafafa)' : '#fff',
        textAlign: 'center',
      }}
    >
      <Text strong style={{ fontSize: 13 }}>
        {card.position_code}
      </Text>
      <div style={{ marginTop: 4 }}>
        {vacant ? <Tag>Vacant</Tag> : <Text style={{ fontSize: 12 }}>{card.component_number}</Text>}
      </div>
      {!vacant && (
        <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 6 }}>
          {card.condition_code && <Tag style={{ margin: 0 }}>{card.condition_code}</Tag>}
          <Text type="secondary" style={{ fontSize: 11 }}>
            V:{' '}
            {card.latest_voltage != null
              ? card.latest_voltage
              : card.nominal_voltage != null
                ? `~${card.nominal_voltage}`
                : '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            SOH: {card.latest_soh_pct != null ? `${card.latest_soh_pct}%` : '—'}
          </Text>
          {card.latest_health_source && (
            <Tag style={{ margin: 0 }}>{card.latest_health_source}</Tag>
          )}
          {card.battery_type && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {card.battery_type.replace(/_/g, ' ')}
            </Text>
          )}
        </Space>
      )}
    </div>
  );
};

export const BatterySchematicView: React.FC<{
  data: BatterySchematicResponse | null | undefined;
  pmvProfileId?: string;
  onLayoutApplied?: () => void;
  onSelectCard?: (card: BatterySchematicCard) => void;
  allowVacantSelect?: boolean;
}> = ({ data, pmvProfileId, onLayoutApplied, onSelectCard, allowVacantSelect }) => {
  if (!data) return null;
  if (data.empty_reason) {
    if (pmvProfileId) {
      return (
        <PmvApplyLayoutEmpty
          profileId={pmvProfileId}
          description={data.empty_reason}
          onApplied={onLayoutApplied}
        />
      );
    }
    return <Empty description={data.empty_reason} />;
  }
  if (!data.cards?.length) {
    return <Empty description="No battery positions on this equipment layout" />;
  }
  return (
    <div
      role="region"
      aria-label="Battery position schematic"
      style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}
    >
      <Text type="secondary">
        Battery bank — operational map (not CAD)
        {allowVacantSelect ? '. Click vacant to install, occupied to open.' : ''}
      </Text>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
        {data.cards.map((c) => {
          const vacant = c.status === 'VACANT';
          const canClick = vacant ? Boolean(allowVacantSelect) : Boolean(c.component_id);
          return (
            <button
              key={c.position_id}
              type="button"
              onClick={() => canClick && onSelectCard?.(c)}
              disabled={!canClick}
              style={{
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: canClick ? 'pointer' : 'default',
              }}
            >
              <BatteryCard card={c} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BatterySchematicView;
